import React, { useEffect } from 'react';
import { useGaEventPageView } from 'src/utils/gaEvent';
import { AssessmentCardSkeleton, Poppins, Spacer } from 'src/common';
import { useAuth } from 'src/state/auth';
import { useNavigate } from 'react-router-dom';
import { AssessmentSelect } from 'src/components/select';
import { useInsightsQuery } from 'src/graphql/_generated';
import useSelectedAssessment from '../useSelectedAssessment';
import { TileContainer } from 'src/components/Tile';
import _ from 'lodash';
import moment from 'moment';
import {
  TileAIF,
  TileControlImplementation,
  TileExpectedLoss,
  TileReview,
  TileRiskAvoidance,
  TileRiskyScenarios,
} from './widgets';
import { isQueryLoading } from 'src/graphql';
import { calculateMeanDaysBetween } from '../util';

const V1 = () => {
  const { features } = useAuth();
  const navigate = useNavigate();
  useGaEventPageView('Cydea | Insights');

  const { setSelectedAssessmentStored, selectedAssessment } = useSelectedAssessment();

  const { data, loading, networkStatus } = useInsightsQuery({
    variables: {
      id: selectedAssessment,
    },
    skip: !selectedAssessment,
  });
  const isLoading = isQueryLoading({ loading, data, networkStatus });

  useEffect(() => {
    if (!features['app.insights']) {
      navigate('/');
    }
  }, [features['app.insights'], navigate]);

  const controls = _.uniqBy(
    _.flatMap(data?.assessment?.scenarios || [], (el) =>
      _.compact(_.map(el.riskManagementPlans, (plan) => plan.control)),
    ),
    'id',
  );

  const totalIncidents = _.uniqBy(_.compact(_.flatMap(data?.assessment?.scenarios || [], (el) => el.incidents)), 'id');
  const nearMisses = totalIncidents.filter((el) => el.incidentType === 'Near miss');
  const incidents = totalIncidents.filter((el) => el.incidentType === 'Incident');

  const insights = {
    expectedLoss: data?.assessment?.AssessmentLiveRisk?.expectedLoss || 0,
    riskyScenarios: _.orderBy(
      data?.assessment?.scenarios.map((scenario) => ({
        id: scenario.id!,
        name: scenario.name!,
        expectedLoss: scenario.liveRisk?.expectedLoss || 0,
      })) || [],
      ['expectedLoss'],
      ['desc'],
    ).slice(0, 3),
    reviewOverdue:
      _.filter(data?.assessment?.scenarios, (el) => el.reviewDate && moment(el.reviewDate).isBefore(moment())).length ||
      0,
    controlImplementation: {
      controls: controls.length,
      live: controls.filter((el) => el.status === 'Live').length,
    },
    aif: {
      meanDaysBetweenIncidents: calculateMeanDaysBetween(incidents),
      meanDaysBetweenNearMisses: calculateMeanDaysBetween(nearMisses),
    },
    riskAvoidance: {
      impactLower: nearMisses.reduce((acc, el) => acc + (el.financialImpactLower || 0), 0),
      impactUpper: nearMisses.reduce((acc, el) => acc + (el.financialImpactUpper || 0), 0),
      nearMisses: nearMisses.length,
    },
  };

  const isEmpty = !isLoading && !selectedAssessment;

  return (
    <>
      <div className="panel">
        <Poppins px={32} weight={600}>
          Insights
        </Poppins>
        {!isEmpty && (
          <div className="panel">
            <AssessmentSelect value={selectedAssessment} onChange={(opt) => setSelectedAssessmentStored(opt.value)} />
          </div>
        )}
      </div>
      <Spacer $px={30} />
      {isLoading ? (
        <TileContainer>
          <AssessmentCardSkeleton />
          <AssessmentCardSkeleton />
          <AssessmentCardSkeleton />
          <AssessmentCardSkeleton />
          <AssessmentCardSkeleton />
          <AssessmentCardSkeleton />
        </TileContainer>
      ) : (
        <>
          {selectedAssessment ? (
            <TileContainer>
              <TileExpectedLoss expectedLoss={insights.expectedLoss} />
              <TileRiskyScenarios scenarios={insights.riskyScenarios} assessmentId={selectedAssessment} />
              <TileReview overdue={insights.reviewOverdue} />
              <TileControlImplementation
                controls={insights.controlImplementation.controls}
                live={insights.controlImplementation.live}
              />
              <TileAIF
                meanDaysBetweenIncidents={insights.aif.meanDaysBetweenIncidents}
                meanDaysBetweenNearMisses={insights.aif.meanDaysBetweenNearMisses}
                hasIncidents={!!totalIncidents.length}
              />
              <TileRiskAvoidance
                impactLower={insights.riskAvoidance.impactLower}
                impactUpper={insights.riskAvoidance.impactUpper}
                nearMisses={insights.riskAvoidance.nearMisses}
              />
            </TileContainer>
          ) : (
            <Poppins>No assessments available</Poppins>
          )}
        </>
      )}
    </>
  );
};

export default V1;
